import React, { useState, useEffect } from "react";
import {
 Row,
 Col,
 Card,
 Button,
 Form,
 Table,
 Badge,
 Spinner,
} from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import PageTitle from "../../layouts/PageTitle";
import api, { getFileUrl } from "../../../api";
import { createQCInspection, updateQCInspection, getQCInspection } from "../../../services/qcService";

const INSPECTION_TYPES = [
 "Field Inspection",
 "Lab Test",
 "Material Inspection",
 "Process Inspection",
 "Final Inspection",
 "In-Process Inspection",
 "Third Party Inspection",
 "Other",
];

const OVERALL_RESULTS = ["Pass", "Fail", "Conditional Pass", "Pending"];

const initialObs = {
 parameter: "",
 specifiedValue: "",
 observedValue: "",
 unit: "",
 result: "Pending",
 remarks: "",
};

const initialNcrReport = {
 details: "",
 correctiveAction: "",
 remarks: "",
 closedDate: "",
 raisePhotos: [],
 closePhotos: [],
};

const initialForm = {
 inspectionDate: new Date().toISOString().slice(0, 10),
 inspectionType: "Field Inspection",
 activityOrScope: "",
 location: "",
 referenceStandard: "",
 overallResult: "Pending",
 nonConformanceRaised: false,
 nonConformanceDetails: "",
 correctiveAction: "",
 ncrClosedDate: "",
 ncrReports: [],
 remarks: "",
 nextInspectionDate: "",
 status: "Draft",
 project: "",
 workOrder: "",
 inspectedBy: "",
 observations: [],
};

const QCForm = ({ mode }) => {
 const navigate = useNavigate();
 const { id } = useParams();
 const isEdit = mode === "edit";

 const [form, setForm] = useState(initialForm);
 const [loading, setLoading] = useState(false);
 const [fetching, setFetching] = useState(false);
 const [projects, setProjects] = useState([]);
 const [workOrders, setWorkOrders] = useState([]);
 const [users, setUsers] = useState([]);
 const [ncrPhotoFiles, setNcrPhotoFiles] = useState({});
 const [ncrPhotoPreviews, setNcrPhotoPreviews] = useState({});

 /* â”€â”€ Load reference data â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
 useEffect(() => {
 const load = async () => {
 try {
 const [pRes, uRes] = await Promise.all([
 api.get("/projects?limit=500"),
 api.get("/users?limit=500"),
 ]);
 setProjects(pRes.data?.data || []);
 setUsers(uRes.data?.data || []);
 } catch (_) {}
 };
 load();
 }, []);

 /* â”€â”€ Load work orders when project changes â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
 useEffect(() => {
 if (!form.project) {
 setWorkOrders([]);
 return;
 }
 api
 .get("/work-orders", { params: { project: form.project, limit: 500 } })
 .then((res) => setWorkOrders(res.data?.data || []))
 .catch(() => {});
 }, [form.project]);

 useEffect(() => {
 const nextPreviews = {};
 Object.entries(ncrPhotoFiles).forEach(([key, files]) => {
 nextPreviews[key] = files.map((file) => ({
 file,
 url: URL.createObjectURL(file),
 }));
 });
 setNcrPhotoPreviews(nextPreviews);

 return () => {
 Object.values(nextPreviews).flat().forEach((preview) => {
 URL.revokeObjectURL(preview.url);
 });
 };
 }, [ncrPhotoFiles]);

 /* â”€â”€ Load record when editing â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
 useEffect(() => {
 if (!isEdit || !id) return;
 setFetching(true);
 getQCInspection(id)
 .then((res) => {
 const r = res.data?.data;
 if (!r) return;
 setForm({
 inspectionDate: r.inspectionDate ? r.inspectionDate.slice(0, 10) : "",
 inspectionType: r.inspectionType || "Field Inspection",
 activityOrScope: r.activityOrScope || "",
 location: r.location || "",
 referenceStandard: r.referenceStandard || "",
 overallResult: r.overallResult || "Pending",
 nonConformanceRaised: r.nonConformanceRaised || false,
 nonConformanceDetails: r.nonConformanceDetails || "",
 correctiveAction: r.correctiveAction || "",
 ncrClosedDate: r.ncrClosedDate ? r.ncrClosedDate.slice(0, 10) : "",
 ncrReports: Array.isArray(r.ncrReports) && r.ncrReports.length
 ? r.ncrReports.map((report) => ({
 details: report.details || "",
 correctiveAction: report.correctiveAction || "",
 remarks: report.remarks || "",
 closedDate: report.closedDate ? report.closedDate.slice(0, 10) : "",
 raisePhotos: Array.isArray(report.raisePhotos) ? report.raisePhotos : [],
 closePhotos: Array.isArray(report.closePhotos) ? report.closePhotos : [],
 }))
 : (r.nonConformanceRaised
 ? [{
 details: r.nonConformanceDetails || "",
 correctiveAction: r.correctiveAction || "",
 remarks: r.remarks || "",
 closedDate: r.ncrClosedDate ? r.ncrClosedDate.slice(0, 10) : "",
 raisePhotos: [],
 closePhotos: [],
 }]
 : []),
 remarks: r.remarks || "",
 nextInspectionDate: r.nextInspectionDate ? r.nextInspectionDate.slice(0, 10) : "",
 status: r.status || "Draft",
 project: r.project?._id || r.project || "",
 workOrder: r.workOrder?._id || r.workOrder || "",
 inspectedBy: r.inspectedBy?._id || r.inspectedBy || "",
 observations: (r.observations || []).map((o) => ({ ...o })),
 });
 })
 .catch(() => toast.error("Failed to load record"))
 .finally(() => setFetching(false));
 }, [isEdit, id]);

 /* â”€â”€ Handlers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
   const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "nonConformanceRaised" && type === "checkbox") {
      setForm((prev) => ({
        ...prev,
        nonConformanceRaised: checked,
        ncrReports: checked
          ? (prev.ncrReports.length === 0 ? [{ ...initialNcrReport }] : prev.ncrReports)
          : [],
        nonConformanceDetails: checked ? prev.nonConformanceDetails : "",
        correctiveAction: checked ? prev.correctiveAction : "",
        ncrClosedDate: checked ? prev.ncrClosedDate : "",
      }));
      if (!checked) setNcrPhotoFiles({});
      return;
    }
    const val = type === "checkbox" ? checked : value;
    setForm((prev) => {
      const next = { ...prev, [name]: val };
      if (name === "project") {
        if (val && prev.workOrder) {
          const currentWo = workOrders.find((w) => String(w._id) === String(prev.workOrder));
          const currentWoProjId = currentWo?.project?._id || currentWo?.project;
          if (currentWoProjId && String(currentWoProjId) !== String(val)) {
            next.workOrder = "";
          }
        }
      } else if (name === "workOrder") {
        if (val) {
          const selectedWo = workOrders.find((w) => String(w._id) === String(val));
          const autoProjId = selectedWo?.project?._id || selectedWo?.project;
          if (autoProjId) {
            next.project = String(autoProjId);
          }
        }
      }
      return next;
    });
  };

 /* â”€â”€ Observation handlers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
 const addObservation = () => {
 setForm((prev) => ({
 ...prev,
 observations: [...prev.observations, { ...initialObs }],
 }));
 };

 const removeObservation = (idx) => {
 setForm((prev) => ({
 ...prev,
 observations: prev.observations.filter((_, i) => i !== idx),
 }));
 };

 const updateObs = (idx, field, value) => {
 setForm((prev) => {
 const obs = [...prev.observations];
 obs[idx] = { ...obs[idx], [field]: value };

 // Auto-compute overallResult
 const results = obs.map((o) => o.result);
 let overall = "Pending";
 if (results.length > 0) {
 if (results.every((r) => r === "Pass")) overall = "Pass";
 else if (results.some((r) => r === "Fail")) overall = "Fail";
 else if (results.every((r) => r !== "Pending")) overall = "Conditional Pass";
 }
 return { ...prev, observations: obs, overallResult: overall };
 });
 };

 const addNcrReport = () => {
 setForm((prev) => ({
 ...prev,
 nonConformanceRaised: true,
 ncrReports: [...prev.ncrReports, { ...initialNcrReport }],
 }));
 };

 const removeNcrReport = (idx) => {
 setForm((prev) => ({
 ...prev,
 ncrReports: prev.ncrReports.filter((_, i) => i !== idx),
 }));
 setNcrPhotoFiles((prev) => {
 const next = {};
 Object.entries(prev).forEach(([key, files]) => {
 const [reportIndexStr, stage] = key.split("-");
 const reportIndex = Number.parseInt(reportIndexStr, 10);
 if (Number.isNaN(reportIndex)) return;
 if (reportIndex < idx) {
 next[key] = files;
 } else if (reportIndex > idx) {
 next[`${reportIndex - 1}-${stage}`] = files;
 }
 });
 return next;
 });
 };

 const updateNcrReport = (idx, field, value) => {
 setForm((prev) => {
 const reports = [...prev.ncrReports];
 reports[idx] = { ...reports[idx], [field]: value };
 return { ...prev, ncrReports: reports, nonConformanceRaised: reports.length > 0 };
 });
 };

 const setReportFiles = (idx, stage, files) => {
 setNcrPhotoFiles((prev) => ({
 ...prev,
 [`${idx}-${stage}`]: [
 ...(prev[`${idx}-${stage}`] || []),
 ...files.filter((file) => !((prev[`${idx}-${stage}`] || []).some((existing) =>
 existing.name === file.name &&
 existing.size === file.size &&
 existing.lastModified === file.lastModified
 ))),
 ],
 }));
 };

 const removeReportFile = (idx, stage, fileIndex) => {
 const key = `${idx}-${stage}`;
 setNcrPhotoFiles((prev) => ({
 ...prev,
 [key]: (prev[key] || []).filter((_, i) => i !== fileIndex),
 }));
 };

 const renderExistingPhotos = (photos, label) => {
 if (!Array.isArray(photos) || photos.length === 0) return null;
 return (
 <div className="mt-3">
 <div className="fw-semibold mb-2">{label}</div>
 <div className="d-flex flex-wrap gap-2">
 {photos.map((photo) => (
 <a
 key={photo._id || photo.fileName}
 href={getFileUrl(photo.fileUrl)}
 target="_blank"
 rel="noreferrer"
 className="d-block text-center"
 style={{ width: 92 }}
 >
 <img
 src={getFileUrl(photo.fileUrl)}
 alt={photo.description || photo.fileName}
 className="rounded border"
 style={{ width: 84, height: 64, objectFit: "cover" }}
 onError={(e) => {
 e.currentTarget.onerror = null;
 e.currentTarget.src = "/logo192.png";
 }}
 />
 <small
 className="d-block mt-1 text-muted"
 style={{ maxWidth: 84, fontSize: "0.7rem", wordBreak: "break-word" }}
 >
 {photo.fileName || "Image"}
 </small>
 </a>
 ))}
 </div>
 </div>
 );
 };

 /* â”€â”€ Submit â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
 const handleSubmit = async (e) => {
 e.preventDefault();
 if (!form.activityOrScope.trim()) {
 toast.error("Activity / Scope is required");
 return;
 }
 if (!form.project) {
 toast.error("Project is required");
 return;
 }
 if (form.nonConformanceRaised && form.ncrReports.length === 0) {
 toast.error("Add at least one NCR report");
 return;
 }

 const normalizedNcrReports = (form.ncrReports || [])
 .map((report) => ({
 details: report.details?.trim() || "",
 correctiveAction: report.correctiveAction?.trim() || "",
 remarks: report.remarks?.trim() || "",
 closedDate: report.closedDate || "",
 raisePhotos: Array.isArray(report.raisePhotos) ? report.raisePhotos : [],
 closePhotos: Array.isArray(report.closePhotos) ? report.closePhotos : [],
 }))
 .filter((report) => report.details || report.correctiveAction || report.remarks || report.closedDate);

 const formData = new FormData();
 const fields = { ...form };
 delete fields.observations;
 delete fields.ncrReports;
 delete fields.ncrClosedDate;

 Object.entries(fields).forEach(([k, v]) => {
 if (v !== "" && v !== null && v !== undefined) {
 formData.append(k, v);
 }
 });

 formData.append("observations", JSON.stringify(form.observations));
 formData.append("ncrReports", JSON.stringify(normalizedNcrReports));

 if (normalizedNcrReports.length > 0) {
 formData.append("nonConformanceDetails", normalizedNcrReports[0].details || "");
 formData.append("correctiveAction", normalizedNcrReports[0].correctiveAction || "");
 }

 const ncrPhotoMeta = [];
 normalizedNcrReports.forEach((report, idx) => {
 (ncrPhotoFiles[`${idx}-raise`] || []).forEach((file) => {
 ncrPhotoMeta.push({ reportIndex: idx, stage: "raise", description: file.name });
 formData.append("ncrPhotos", file);
 });
 (ncrPhotoFiles[`${idx}-close`] || []).forEach((file) => {
 ncrPhotoMeta.push({ reportIndex: idx, stage: "close", description: file.name });
 formData.append("ncrPhotos", file);
 });
 });

 if (ncrPhotoMeta.length > 0) {
 formData.append("ncrPhotoMeta", JSON.stringify(ncrPhotoMeta));
 }

 setLoading(true);
 try {
 if (isEdit) {
 await updateQCInspection(id, formData);
 toast.success("QC Inspection updated");
 } else {
 await createQCInspection(formData);
 toast.success("QC Inspection created");
 }
 navigate("../qc");
 } catch (err) {
 toast.error(err?.response?.data?.message || "Failed to save");
 } finally {
 setLoading(false);
 }
 };

 if (fetching) {
 return (
 <div className="text-center py-5">
 <Spinner animation="border" variant="primary" />
 </div>
 );
 }

 return (
 <>
 <PageTitle
 activeMenu={isEdit ? "Edit QC Inspection" : "New QC Inspection"}
 motherMenu="QC"
 />

 <Form onSubmit={handleSubmit}>
 {/* â”€â”€ Section 1: Core Details â”€â”€ */}
 <Card className="shadow-sm mb-3">
 <Card.Header>
 <h6 className="mb-0">
 <i className="fas fa-clipboard-check me-2 text-primary" />
 Inspection Details
 </h6>
 </Card.Header>
 <Card.Body>
 <Row className="mb-3">
 <Col md={3}>
 <Form.Group>
 <Form.Label>
 Inspection Date <span className="text-danger">*</span>
 </Form.Label>
 <Form.Control
 type="date"
 name="inspectionDate"
 value={form.inspectionDate}
 onChange={handleChange}
 required
 />
 </Form.Group>
 </Col>
 <Col md={3}>
 <Form.Group>
 <Form.Label>Inspection Type</Form.Label>
 <Form.Select
 name="inspectionType"
 value={form.inspectionType}
 onChange={handleChange}
 >
 {INSPECTION_TYPES.map((t) => (
 <option key={t} value={t}>{t}</option>
 ))}
 </Form.Select>
 </Form.Group>
 </Col>
 <Col md={3}>
 <Form.Group>
 <Form.Label>Status</Form.Label>
 <Form.Select name="status" value={form.status} onChange={handleChange}>
 {["Draft", "Submitted", "Closed"].map((s) => (
 <option key={s} value={s}>{s}</option>
 ))}
 </Form.Select>
 </Form.Group>
 </Col>
 <Col md={3}>
 <Form.Group>
 <Form.Label>Overall Result</Form.Label>
 <Form.Select name="overallResult" value={form.overallResult} onChange={handleChange}>
 {OVERALL_RESULTS.map((r) => (
 <option key={r} value={r}>{r}</option>
 ))}
 </Form.Select>
 </Form.Group>
 </Col>
 </Row>

 <Row className="mb-3">
 <Col md={6}>
 <Form.Group>
 <Form.Label>
 Activity / Scope <span className="text-danger">*</span>
 </Form.Label>
 <Form.Control
 name="activityOrScope"
 value={form.activityOrScope}
 onChange={handleChange}
 placeholder="e.g. Concrete casting slab, Cable laying trench"
 required
 />
 </Form.Group>
 </Col>
 <Col md={3}>
 <Form.Group>
 <Form.Label>Location / Grid Ref</Form.Label>
 <Form.Control
 name="location"
 value={form.location}
 onChange={handleChange}
 placeholder="e.g. Block A, Floor 3"
 />
 </Form.Group>
 </Col>
 <Col md={3}>
 <Form.Group>
 <Form.Label>Reference Standard</Form.Label>
 <Form.Control
 name="referenceStandard"
 value={form.referenceStandard}
 onChange={handleChange}
 placeholder="e.g. IS 456, ASTM A615"
 />
 </Form.Group>
 </Col>
 </Row>

 <Row className="mb-3">
 <Col md={4}>
 <Form.Group>
 <Form.Label>
 Project <span className="text-danger">*</span>
 </Form.Label>
 <Form.Select name="project" value={form.project} onChange={handleChange} required>
 <option value="">Select Project</option>
 {projects.map((p) => (
 <option key={p._id} value={p._id}>
 {p.projectName || p.name}
 </option>
 ))}
 </Form.Select>
 </Form.Group>
 </Col>
 <Col md={4}>
 <Form.Group>
 <Form.Label>Work Order</Form.Label>
 <Form.Select name="workOrder" value={form.workOrder} onChange={handleChange}>
 <option value="">Select Work Order</option>
 {workOrders.map((wo) => (
 <option key={wo._id} value={wo._id}>
 {wo.woNo}{wo.label ? ` – ${wo.label}` : ""}
 </option>
 ))}
 </Form.Select>
 </Form.Group>
 </Col>
 <Col md={4}>
 <Form.Group>
 <Form.Label>Inspected By</Form.Label>
 <Form.Select name="inspectedBy" value={form.inspectedBy} onChange={handleChange}>
 <option value="">Select User</option>
 {users.map((u) => (
 <option key={u._id} value={u._id}>{u.name}</option>
 ))}
 </Form.Select>
 </Form.Group>
 </Col>
 </Row>
 </Card.Body>
 </Card>

 {/* â”€â”€ Section 2: Observations â”€â”€ */}
 <Card className="shadow-sm mb-3">
 <Card.Header className="d-flex justify-content-between align-items-center">
 <h6 className="mb-0">
 <i className="fas fa-list-check me-2 text-primary" />
 Observations / Test Parameters
 <Badge bg="secondary" className="ms-2">{form.observations.length}</Badge>
 </h6>
 <Button size="sm" variant="outline-primary" type="button" onClick={addObservation}>
 <i className="fas fa-plus me-1" /> Add Row
 </Button>
 </Card.Header>
 <Card.Body className="p-0">
 {form.observations.length === 0 ? (
 <div className="text-center py-4 text-muted">
 <i className="fas fa-table fa-2x mb-2 d-block" />
 No observations added yet. Click "Add Row" to start.
 </div>
 ) : (
 <div className="table-responsive">
 <Table size="sm" className="mb-0 align-middle">
 <thead className="table-light">
 <tr>
 <th style={{ width: 200 }}>Parameter</th>
 <th style={{ width: 140 }}>Specified Value</th>
 <th style={{ width: 140 }}>Observed Value</th>
 <th style={{ width: 80 }}>Unit</th>
 <th style={{ width: 120 }}>Result</th>
 <th>Remarks</th>
 <th style={{ width: 40 }}></th>
 </tr>
 </thead>
 <tbody>
 {form.observations.map((obs, idx) => (
 <tr key={idx}>
 <td>
 <Form.Control
 size="sm"
 value={obs.parameter}
 onChange={(e) => updateObs(idx, "parameter", e.target.value)}
 placeholder="e.g. Slump Value"
 />
 </td>
 <td>
 <Form.Control
 size="sm"
 value={obs.specifiedValue}
 onChange={(e) => updateObs(idx, "specifiedValue", e.target.value)}
 placeholder="e.g. 75-100"
 />
 </td>
 <td>
 <Form.Control
 size="sm"
 value={obs.observedValue}
 onChange={(e) => updateObs(idx, "observedValue", e.target.value)}
 placeholder="Actual"
 />
 </td>
 <td>
 <Form.Control
 size="sm"
 value={obs.unit}
 onChange={(e) => updateObs(idx, "unit", e.target.value)}
 placeholder="mm"
 />
 </td>
 <td>
 <Form.Select
 size="sm"
 value={obs.result}
 onChange={(e) => updateObs(idx, "result", e.target.value)}
 >
 {["Pending", "Pass", "Fail", "NA"].map((r) => (
 <option key={r} value={r}>{r}</option>
 ))}
 </Form.Select>
 </td>
 <td>
 <Form.Control
 size="sm"
 value={obs.remarks}
 onChange={(e) => updateObs(idx, "remarks", e.target.value)}
 placeholder="Optional"
 />
 </td>
 <td>
 <Button
 size="sm"
 variant="outline-danger"
 type="button"
 onClick={() => removeObservation(idx)}
 >
 <i className="fas fa-trash" />
 </Button>
 </td>
 </tr>
 ))}
 </tbody>
 </Table>
 </div>
 )}
 </Card.Body>
 </Card>

 {/* â”€â”€ Section 3: NCR â”€â”€ */}
 <Card className="shadow-sm mb-3">
 <Card.Header>
 <h6 className="mb-0">
 <i className="fas fa-exclamation-triangle me-2 text-warning" />
 Non-Conformance Report (NCR)
 </h6>
 </Card.Header>
 <Card.Body>
 <Row className="mb-3">
 <Col md={12}>
 <Form.Check
 type="switch"
 id="nonConformanceRaised"
 name="nonConformanceRaised"
 label="Non-Conformance Raised"
 checked={form.nonConformanceRaised}
 onChange={handleChange}
 className="mb-3"
 />
 </Col>
 </Row>
 {form.nonConformanceRaised && (
 <div>
 <div className="d-flex justify-content-between align-items-center mb-3">
 <div>
 <strong className="d-block">NCR Reports</strong>
 <small className="text-muted">
 Add one or more non-conformance entries. Close date is set automatically.
 </small>
 </div>
 <Button size="sm" variant="outline-warning" type="button" onClick={addNcrReport}>
 <i className="fas fa-plus me-1" /> Add NCR Report
 </Button>
 </div>

 {form.ncrReports.length === 0 ? (
 <div className="text-center py-4 text-muted border rounded bg-light">
 <i className="fas fa-file-alt fa-2x mb-2 d-block" />
 No NCR reports added yet.
 </div>
 ) : (
 <div className="d-flex flex-column gap-3">
 {form.ncrReports.map((report, idx) => (
 <Card key={idx} className="border">
 <Card.Header className="d-flex justify-content-between align-items-center py-2">
 <strong>NCR Report {idx + 1}</strong>
 <Button
 size="sm"
 variant="outline-danger"
 type="button"
 onClick={() => removeNcrReport(idx)}
 >
 <i className="fas fa-trash me-1" /> Remove
 </Button>
 </Card.Header>
 <Card.Body>
 <Row className="g-3">
 <Col md={6}>
 <Form.Group>
 <Form.Label>Non-Conformance Details</Form.Label>
 <Form.Control
 as="textarea"
 rows={3}
 value={report.details}
 onChange={(e) => updateNcrReport(idx, "details", e.target.value)}
 placeholder="Describe the non-conformance..."
 />
 </Form.Group>
 </Col>
 <Col md={6}>
 <Form.Group>
 <Form.Label>Corrective Action</Form.Label>
 <Form.Control
 as="textarea"
 rows={3}
 value={report.correctiveAction}
 onChange={(e) =>
 updateNcrReport(idx, "correctiveAction", e.target.value)
 }
 placeholder="Action taken / planned..."
 />
 </Form.Group>
 </Col>
 <Col md={12}>
 <Form.Group>
 <Form.Label>Remarks</Form.Label>
 <Form.Control
 as="textarea"
 rows={2}
 value={report.remarks}
 onChange={(e) => updateNcrReport(idx, "remarks", e.target.value)}
 placeholder="Optional remarks..."
 />
 </Form.Group>
 </Col>
 <Col md={12}>
 {renderExistingPhotos(report.raisePhotos, "Existing Raise Images")}
 {renderExistingPhotos(report.closePhotos, "Existing Close Images")}
 </Col>
 <Col md={6}>
 <Form.Group>
 <Form.Label>Raise Time Images</Form.Label>
 <Form.Control
 type="file"
 accept="image/*"
 multiple
 onChange={(e) =>
 setReportFiles(idx, "raise", Array.from(e.target.files || []))
 }
 />
 <Form.Text className="text-muted">
 Upload images while raising this NCR.
 </Form.Text>
 {(ncrPhotoPreviews[`${idx}-raise`] || []).length > 0 && (
 <div className="mt-2 d-flex flex-wrap gap-2">
 {(ncrPhotoPreviews[`${idx}-raise`] || []).map((preview, fileIdx) => (
 <div
 key={`${preview.file.name}-${fileIdx}`}
 className="position-relative text-center"
 style={{ width: 90 }}
 >
 <a href={preview.url} target="_blank" rel="noreferrer">
 <img
 src={preview.url}
 alt={preview.file.name}
 className="rounded border"
 style={{ width: 84, height: 64, objectFit: "cover" }}
 />
 </a>
 <small
 className="d-block mt-1 text-muted"
 style={{ maxWidth: 84, fontSize: "0.7rem", wordBreak: "break-word" }}
 >
 {preview.file.name}
 </small>
 <Button
 type="button"
 size="sm"
 variant="danger"
 className="position-absolute top-0 end-0 p-0"
 style={{ width: 20, height: 20, fontSize: 10, lineHeight: 1 }}
 onClick={() => removeReportFile(idx, "raise", fileIdx)}
 >
 x
 </Button>
 </div>
 ))}
 </div>
 )}
 </Form.Group>
 </Col>
 <Col md={6}>
 <Form.Group>
 <Form.Label>Close Time Images</Form.Label>
 <Form.Control
 type="file"
 accept="image/*"
 multiple
 onChange={(e) =>
 setReportFiles(idx, "close", Array.from(e.target.files || []))
 }
 />
 <Form.Text className="text-muted">
 Optional images for the close action.
 </Form.Text>
 {(ncrPhotoPreviews[`${idx}-close`] || []).length > 0 && (
 <div className="mt-2 d-flex flex-wrap gap-2">
 {(ncrPhotoPreviews[`${idx}-close`] || []).map((preview, fileIdx) => (
 <div
 key={`${preview.file.name}-${fileIdx}`}
 className="position-relative text-center"
 style={{ width: 90 }}
 >
 <a href={preview.url} target="_blank" rel="noreferrer">
 <img
 src={preview.url}
 alt={preview.file.name}
 className="rounded border"
 style={{ width: 84, height: 64, objectFit: "cover" }}
 />
 </a>
 <small
 className="d-block mt-1 text-muted"
 style={{ maxWidth: 84, fontSize: "0.7rem", wordBreak: "break-word" }}
 >
 {preview.file.name}
 </small>
 <Button
 type="button"
 size="sm"
 variant="danger"
 className="position-absolute top-0 end-0 p-0"
 style={{ width: 20, height: 20, fontSize: 10, lineHeight: 1 }}
 onClick={() => removeReportFile(idx, "close", fileIdx)}
 >
 x
 </Button>
 </div>
 ))}
 </div>
 )}
 </Form.Group>
 </Col>
 </Row>
 </Card.Body>
 </Card>
 ))}
 </div>
 )}
 </div>
 )}
 </Card.Body>
 </Card>

 {/* â”€â”€ Section 4: Additional Info â”€â”€ */}
 <Card className="shadow-sm mb-3">
 <Card.Header>
 <h6 className="mb-0">
 <i className="fas fa-info-circle me-2 text-info" />
 Additional Information
 </h6>
 </Card.Header>
 <Card.Body>
 <Row className="mb-3">
 <Col md={8}>
 <Form.Group>
 <Form.Label>Remarks</Form.Label>
 <Form.Control
 as="textarea"
 rows={3}
 name="remarks"
 value={form.remarks}
 onChange={handleChange}
 placeholder="Any additional notes or observations..."
 />
 </Form.Group>
 </Col>
 <Col md={4}>
 <Form.Group>
 <Form.Label>Next Inspection Date</Form.Label>
 <Form.Control
 type="date"
 name="nextInspectionDate"
 value={form.nextInspectionDate}
 onChange={handleChange}
 />
 </Form.Group>
 </Col>
 </Row>

 {/* NCR images are uploaded inside each report card */}
 </Card.Body>
 </Card>

 {/* â”€â”€ Submit â”€â”€ */}
 <div className="d-flex gap-2 mb-4">
 <Button type="submit" variant="primary" disabled={loading}>
 {loading ? (
 <>
 <Spinner size="sm" className="me-2" />
 {isEdit ? "Updating..." : "Saving..."}
 </>
 ) : isEdit ? (
 "Update Inspection"
 ) : (
 "Create Inspection"
 )}
 </Button>
 <Button
 variant="outline-secondary"
 type="button"
 onClick={() => navigate("../qc")}
 >
 Cancel
 </Button>
 </div>
 </Form>
 </>
 );
};

export default QCForm;

